import React, { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import type { Chart } from '../engine/chart';
import { PLANETS, ASPECT_MEANINGS } from '../data/constants';
import { getAspect } from '../lib/contentLookup';

interface Props { chart: Chart }

/**
 * Advanced-mode aspect list. Sorted by orb tightness so the most defining
 * aspects render first. Each row is a collapsible row that reveals the
 * aspect prose, gift, and cost from the Phase 4 library. Tight aspects
 * (orb < 2°) get the orbAware flourish appended.
 */
export const AspectList: React.FC<Props> = ({ chart }) => {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const sorted = useMemo(
    () => [...(chart.aspects ?? [])].sort((a, b) => a.orb_actual - b.orb_actual),
    [chart],
  );

  if (!sorted.length) {
    return (
      <Card className="p-5 bg-card/50 text-sm text-muted-foreground">
        No major aspects within orb.
      </Card>
    );
  }

  const glyph = (n: string) => PLANETS.find((p) => p.name === n)?.glyph ?? n;
  const color = (n: string) => PLANETS.find((p) => p.name === n)?.color;

  return (
    <Card className="p-5 bg-card/50">
      <ul className="divide-y divide-border/40">
        {sorted.slice(0, 30).map((a, i) => {
          const k = `${a.p1}-${a.name}-${a.p2}-${i}`;
          const isOpen = !!open[k];
          const tight = a.orb_actual < 2;
          const entry = getAspect(a.p1, a.name, a.p2);
          const meaning = ASPECT_MEANINGS[a.name];
          const hasDetail = !!entry || !!meaning;

          return (
            <li key={k}>
              <Collapsible
                open={isOpen}
                onOpenChange={() => hasDetail && setOpen((s) => ({ ...s, [k]: !s[k] }))}
              >
                <CollapsibleTrigger
                  asChild
                  disabled={!hasDetail}
                  className={hasDetail ? 'cursor-pointer' : 'cursor-default'}
                >
                  <div className="flex items-center gap-3 py-2.5 hover:bg-muted/30 px-2 -mx-2 rounded-sm transition-colors">
                    <span className="font-serif text-base shrink-0" style={{ color: color(a.p1) }}>
                      {glyph(a.p1)}
                    </span>
                    <span className="font-serif text-base shrink-0" style={{ color: a.color }}>
                      {a.symbol}
                    </span>
                    <span className="font-serif text-base shrink-0" style={{ color: color(a.p2) }}>
                      {glyph(a.p2)}
                    </span>
                    <span className="text-sm font-medium hidden sm:inline">
                      {a.p1} {a.name} {a.p2}
                    </span>
                    <span className="text-sm font-medium sm:hidden">{a.name}</span>
                    <span className="ml-auto flex items-center gap-2">
                      <span
                        className={`text-xs tabular-nums ${tight ? 'text-primary font-medium' : 'text-muted-foreground'}`}
                      >
                        orb {a.orb_actual.toFixed(2)}°{tight ? ' · tight' : ''}
                      </span>
                      {hasDetail && (
                        <ChevronDown
                          className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        />
                      )}
                    </span>
                  </div>
                </CollapsibleTrigger>
                {hasDetail && (
                  <CollapsibleContent className="pb-4 pt-1 px-2 space-y-2">
                    {entry ? (
                      <>
                        <p className="text-sm font-medium text-foreground/90">{entry.headline}</p>
                        <p className="text-sm text-foreground/85 leading-relaxed">{entry.paragraph}</p>
                        <div className="grid sm:grid-cols-2 gap-2 pt-1">
                          <p className="text-xs text-foreground/80">
                            <span className="uppercase tracking-wider text-muted-foreground">Gift:</span> {entry.gift}
                          </p>
                          <p className="text-xs text-foreground/80">
                            <span className="uppercase tracking-wider text-muted-foreground">Cost:</span> {entry.cost}
                          </p>
                        </div>
                        {tight && entry.orbAware && (
                          <p className="text-xs text-primary/90 border-l-2 border-primary/40 pl-2">
                            <span className="uppercase tracking-wider text-muted-foreground">Tight orb:</span>{' '}
                            {entry.orbAware}
                          </p>
                        )}
                      </>
                    ) : meaning ? (
                      <p className="text-sm text-foreground/85 leading-relaxed italic">
                        {meaning.feel}.
                      </p>
                    ) : null}
                  </CollapsibleContent>
                )}
              </Collapsible>
            </li>
          );
        })}
      </ul>
      {sorted.length > 30 && (
        <p className="mt-3 text-xs text-muted-foreground">
          Showing 30 tightest aspects of {sorted.length} total.
        </p>
      )}
    </Card>
  );
};
